import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { RxCross1 } from "react-icons/rx";
import useClickOutside from "utils/clickOutside";
import { updateInstallmentDueDate } from "redux/actions/installmentActions";
import moment from "moment";
import Status from "components/global/Status";
import formatLabel from "utils/formatLabel";

const EditDueDateModal = ({ installment, onClose, onSuccess }) => {
  const modalRef = useRef(null);
  const dispatch = useDispatch();

  const [dueDate, setDueDate] = useState(
    installment?.dueDate ? moment(installment.dueDate).format("YYYY-MM-DD") : ""
  );
  const [notes, setNotes] = useState(installment?.notes || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useClickOutside(modalRef, () => {
    if (!loading) onClose();
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dueDate) {
      setError("Please select a valid due date.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const updatedDoc = await dispatch(
        updateInstallmentDueDate(installment._id, { dueDate, notes })
      );
      if (onSuccess) {
        onSuccess(updatedDoc);
      }
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to update due date."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm animate-fadeIn">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-gray-100 animate-scaleUp"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              Edit Installment Due Date
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Super Admin Override
            </p>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 h-8 w-8 rounded-lg hover:bg-gray-200/60 flex items-center justify-center transition"
          >
            <RxCross1 size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
          {/* Summary Card */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200/70 grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider block">
                Installment
              </span>
              <span className="font-semibold text-gray-800">
                #{installment?.seq} &bull; {formatLabel(installment?.type)}
              </span>
            </div>

            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider block">
                Current Status
              </span>
              <div className="mt-0.5">
                <Status status={installment?.status} />
              </div>
            </div>

            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider block">
                Total Amount
              </span>
              <span className="font-semibold text-gray-800">
                PKR {Number(installment?.amount || 0).toLocaleString("en-US")}
              </span>
            </div>

            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider block">
                Current Due Date
              </span>
              <span className="font-semibold text-gray-800">
                {installment?.dueDate
                  ? moment(installment.dueDate).format("DD-MM-YYYY")
                  : "-"}
              </span>
            </div>
          </div>

          {/* New Due Date Input */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">
              New Due Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                if (error) setError("");
              }}
              required
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
            />
          </div>

          {/* Remarks / Notes */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">
              Remarks / Reason <span className="text-xs font-normal text-gray-400">(Optional)</span>
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Customer requested extension due to banking clearance..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition resize-none"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-xl shadow-sm transition flex items-center gap-2"
            >
              {loading ? "Updating..." : "Save Due Date"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDueDateModal;
